/**
 * Light Sensor Calculations
 *
 * Comprehensive utility for light/illumination monitoring including:
 * - Lux (Illuminance) measurement
 * - Brightness level classification
 * - Day/night detection
 * - Sun exposure tracking
 * - Circadian rhythm recommendations
 * - Optimal lighting suggestions
 */

// ==================== Types ====================

export type BrightnessLevel =
  | 'dark'
  | 'dim'
  | 'moderate'
  | 'bright'
  | 'very_bright'
  | 'extremely_bright';

export type TimeOfDay = 'night' | 'dawn' | 'morning' | 'noon' | 'afternoon' | 'dusk' | 'evening';

export interface LightReading {
  timestamp: Date;
  lux: number; // Illuminance in lux (0-100,000+)
  uvIndex?: number; // UV Index (0-11+)
  colorTemperature?: number; // Kelvin (2700-6500+)
}

export interface LightStats {
  averageLux: number;
  maxLux: number;
  minLux: number;
  readingCount: number;
  darkHours: number;
  dimHours: number;
  moderateHours: number;
  brightHours: number;
  veryBrightHours: number;
  sunExposureMinutes: number;
  daylightPercentage: number;
  nightHours: number;
  optimalLightingHours: number;
}

export interface LuxHourPattern {
  hour: number;
  averageLux: number;
  maxLux: number;
  minLux: number;
  readingCount: number;
  brightnessLevel: BrightnessLevel;
  isPeakBrightness: boolean;
  timeOfDay: TimeOfDay;
}

export interface CircadianRecommendation {
  time: TimeOfDay;
  recommendedLux: number;
  currentLux: number;
  status: 'optimal' | 'too_dim' | 'too_bright';
  suggestion: string;
  reason: string;
}

export interface LightStatus {
  currentLux: number;
  currentBrightness: BrightnessLevel;
  isDaylight: boolean;
  timeOfDay: TimeOfDay;
  sunExposure: {
    todayMinutes: number;
    weeklyMinutes: number;
    recommendedMinutes: number;
    isAdequate: boolean;
  };
  circadianAlignment: 'good' | 'fair' | 'poor';
  todayStats: LightStats;
  yesterdayStats: LightStats;
  weekStats: LightStats;
  monthStats: LightStats;
  recommendations: string[];
  trend: 'brightening' | 'stable' | 'dimming';
}

// ==================== Core Functions ====================

/**
 * Get brightness level from lux value
 */
export function getBrightnessLevel(lux: number): BrightnessLevel {
  if (lux < 10) return 'dark';
  if (lux < 50) return 'dim';
  if (lux < 500) return 'moderate';
  if (lux < 10000) return 'bright';
  if (lux < 50000) return 'very_bright';
  return 'extremely_bright';
}

/**
 * Determine if it's daylight based on lux
 */
export function isDaylight(lux: number): boolean {
  return lux > 1000; // > 1000 lux typically indicates outdoor daylight
}

/**
 * Get time of day classification based on lux and hour
 */
export function getTimeOfDay(hour: number, lux: number): TimeOfDay {
  // Night (10 PM - 5 AM) and low light
  if ((hour >= 22 || hour < 5) && lux < 50) return 'night';

  // Dawn (5 AM - 7 AM)
  if (hour >= 5 && hour < 7) return 'dawn';

  // Morning (7 AM - 11 AM)
  if (hour >= 7 && hour < 11) return 'morning';

  // Noon (11 AM - 2 PM) with high brightness
  if (hour >= 11 && hour < 14 && lux > 10000) return 'noon';

  // Afternoon (2 PM - 6 PM)
  if (hour >= 14 && hour < 18) return 'afternoon';

  // Dusk (6 PM - 8 PM)
  if (hour >= 18 && hour < 20) return 'dusk';

  // Evening (8 PM - 10 PM)
  if (hour >= 20 && hour < 22) return 'evening';

  return 'night';
}

/**
 * Calculate average lux from readings
 */
export function calculateAverageLux(
  readings: LightReading[],
  startDate: Date,
  endDate: Date,
): number {
  const filtered = readings.filter((r) => r.timestamp >= startDate && r.timestamp <= endDate);

  if (filtered.length === 0) return 0;

  const sum = filtered.reduce((acc, r) => acc + r.lux, 0);
  return Math.round(sum / filtered.length);
}

/**
 * Calculate comprehensive light statistics
 */
export function getLightStatistics(
  readings: LightReading[],
  startDate: Date,
  endDate: Date,
): LightStats {
  const filtered = readings.filter((r) => r.timestamp >= startDate && r.timestamp <= endDate);

  if (filtered.length === 0) {
    return {
      averageLux: 0,
      maxLux: 0,
      minLux: 0,
      readingCount: 0,
      darkHours: 0,
      dimHours: 0,
      moderateHours: 0,
      brightHours: 0,
      veryBrightHours: 0,
      sunExposureMinutes: 0,
      daylightPercentage: 0,
      nightHours: 0,
      optimalLightingHours: 0,
    };
  }

  const luxValues = filtered.map((r) => r.lux);
  const averageLux = Math.round(luxValues.reduce((a, b) => a + b, 0) / luxValues.length);
  const maxLux = Math.max(...luxValues);
  const minLux = Math.min(...luxValues);

  // Count hours per brightness level (assuming readings every minute)
  let darkCount = 0;
  let dimCount = 0;
  let moderateCount = 0;
  let brightCount = 0;
  let veryBrightCount = 0;
  let sunExposureCount = 0;
  let daylightCount = 0;
  let nightCount = 0;
  let optimalCount = 0;

  filtered.forEach((reading) => {
    const level = getBrightnessLevel(reading.lux);
    const hour = reading.timestamp.getHours();

    if (level === 'dark') darkCount++;
    else if (level === 'dim') dimCount++;
    else if (level === 'moderate') moderateCount++;
    else if (level === 'bright') brightCount++;
    else veryBrightCount++;

    if (isDaylight(reading.lux)) {
      daylightCount++;
      sunExposureCount++;
    }

    if ((hour >= 22 || hour < 6) && reading.lux < 10) nightCount++;

    // Optimal lighting: 300-500 lux for general tasks
    if (reading.lux >= 300 && reading.lux <= 500) optimalCount++;
  });

  const totalHours = filtered.length / 60; // Convert minutes to hours
  const daylightPercentage = Math.round((daylightCount / filtered.length) * 100);

  return {
    averageLux,
    maxLux,
    minLux,
    readingCount: filtered.length,
    darkHours: Math.round(darkCount / 60),
    dimHours: Math.round(dimCount / 60),
    moderateHours: Math.round(moderateCount / 60),
    brightHours: Math.round(brightCount / 60),
    veryBrightHours: Math.round(veryBrightCount / 60),
    sunExposureMinutes: sunExposureCount,
    daylightPercentage,
    nightHours: Math.round(nightCount / 60),
    optimalLightingHours: Math.round(optimalCount / 60),
  };
}

/**
 * Get 24-hour light pattern
 */
export function getLuxPattern(readings: LightReading[], date: Date): LuxHourPattern[] {
  const pattern: LuxHourPattern[] = [];
  const dateStart = new Date(date);
  dateStart.setHours(0, 0, 0, 0);

  for (let hour = 0; hour < 24; hour++) {
    const hourStart = new Date(dateStart);
    hourStart.setHours(hour);
    const hourEnd = new Date(hourStart);
    hourEnd.setHours(hour + 1);

    const hourReadings = readings.filter((r) => r.timestamp >= hourStart && r.timestamp < hourEnd);

    if (hourReadings.length > 0) {
      const luxValues = hourReadings.map((r) => r.lux);
      const avgLux = Math.round(luxValues.reduce((a, b) => a + b, 0) / luxValues.length);
      const maxLux = Math.max(...luxValues);
      const minLux = Math.min(...luxValues);

      pattern.push({
        hour,
        averageLux: avgLux,
        maxLux,
        minLux,
        readingCount: hourReadings.length,
        brightnessLevel: getBrightnessLevel(avgLux),
        isPeakBrightness: false,
        timeOfDay: getTimeOfDay(hour, avgLux),
      });
    } else {
      pattern.push({
        hour,
        averageLux: 0,
        maxLux: 0,
        minLux: 0,
        readingCount: 0,
        brightnessLevel: 'dark',
        isPeakBrightness: false,
        timeOfDay: getTimeOfDay(hour, 0),
      });
    }
  }

  // Mark peak brightness hour
  if (pattern.length > 0) {
    const maxAvg = Math.max(...pattern.map((p) => p.averageLux));
    const peakIndex = pattern.findIndex((p) => p.averageLux === maxAvg);
    if (peakIndex >= 0) {
      pattern[peakIndex].isPeakBrightness = true;
    }
  }

  return pattern;
}

/**
 * Generate circadian rhythm recommendations
 */
export function getCircadianRecommendations(
  currentHour: number,
  currentLux: number,
  language: 'en' | 'ar' = 'en',
): CircadianRecommendation[] {
  const recommendations: CircadianRecommendation[] = [];

  // Morning (6-9 AM): Bright blue light for alertness
  if (currentHour >= 6 && currentHour < 9) {
    const recommendedLux = 5000;
    recommendations.push({
      time: 'morning',
      recommendedLux,
      currentLux,
      status: currentLux >= recommendedLux ? 'optimal' : 'too_dim',
      suggestion:
        language === 'ar'
          ? 'تعرّض لإضاءة ساطعة لتحفيز اليقظة'
          : 'Expose yourself to bright light to boost alertness',
      reason:
        language === 'ar'
          ? 'الإضاءة الساطعة صباحاً تساعد على ضبط الساعة البيولوجية'
          : 'Bright morning light helps regulate circadian rhythm',
    });
  }

  // Afternoon (12-3 PM): Maintain alertness
  if (currentHour >= 12 && currentHour < 15) {
    const recommendedLux = 3000;
    recommendations.push({
      time: 'noon',
      recommendedLux,
      currentLux,
      status:
        currentLux >= recommendedLux * 0.8 && currentLux <= recommendedLux * 1.2
          ? 'optimal'
          : currentLux < recommendedLux
          ? 'too_dim'
          : 'too_bright',
      suggestion:
        language === 'ar' ? 'حافظ على إضاءة جيدة للتركيز' : 'Maintain good lighting for focus',
      reason:
        language === 'ar'
          ? 'الإضاءة الكافية تحسّن الإنتاجية'
          : 'Adequate lighting improves productivity',
    });
  }

  // Evening (6-9 PM): Reduce brightness
  if (currentHour >= 18 && currentHour < 21) {
    const recommendedLux = 300;
    recommendations.push({
      time: 'dusk',
      recommendedLux,
      currentLux,
      status: currentLux <= recommendedLux ? 'optimal' : 'too_bright',
      suggestion:
        language === 'ar' ? 'خفّض الإضاءة للاستعداد للنوم' : 'Reduce lighting to prepare for sleep',
      reason:
        language === 'ar'
          ? 'الإضاءة الخافتة تحفّز إفراز الميلاتونين'
          : 'Dim light promotes melatonin production',
    });
  }

  // Night (9 PM - 6 AM): Minimal light
  if (currentHour >= 21 || currentHour < 6) {
    const recommendedLux = 10;
    recommendations.push({
      time: 'night',
      recommendedLux,
      currentLux,
      status: currentLux <= recommendedLux ? 'optimal' : 'too_bright',
      suggestion:
        language === 'ar' ? 'استخدم إضاءة خافتة جداً أو حمراء' : 'Use very dim or red light only',
      reason:
        language === 'ar'
          ? 'الإضاءة الساطعة ليلاً تعطّل النوم'
          : 'Bright light at night disrupts sleep',
    });
  }

  return recommendations;
}

/**
 * Generate lighting recommendations
 */
export function generateLightingRecommendations(
  currentLux: number,
  currentHour: number,
  stats: LightStats,
  language: 'en' | 'ar' = 'en',
): string[] {
  const recommendations: string[] = [];

  if (language === 'ar') {
    // Arabic recommendations
    if (currentLux < 300 && currentHour >= 8 && currentHour < 20) {
      recommendations.push('أضف المزيد من مصادر الإضاءة');
    }

    if (currentLux > 1000 && (currentHour >= 21 || currentHour < 6)) {
      recommendations.push('خفّض الإضاءة - وقت النوم قريب');
    }

    if (stats.daylightPercentage < 50) {
      recommendations.push('احرص على التعرض لضوء الشمس الطبيعي');
    }

    if (stats.sunExposureMinutes < 30) {
      recommendations.push('تحتاج لمزيد من ضوء الشمس (30+ دقيقة يومياً)');
    }

    if (currentLux > 50000) {
      recommendations.push('إضاءة شديدة جداً - ارتدِ نظارة شمسية');
    }

    const brightness = getBrightnessLevel(currentLux);
    if (brightness === 'moderate' && currentHour >= 8 && currentHour < 18) {
      recommendations.push('مستوى الإضاءة مثالي للعمل والقراءة');
    }

    if (stats.optimalLightingHours < 6) {
      recommendations.push('حسّن جودة الإضاءة في مساحة العمل');
    }
  } else {
    // English recommendations
    if (currentLux < 300 && currentHour >= 8 && currentHour < 20) {
      recommendations.push('Add more light sources');
    }

    if (currentLux > 1000 && (currentHour >= 21 || currentHour < 6)) {
      recommendations.push('Reduce lighting - bedtime approaching');
    }

    if (stats.daylightPercentage < 50) {
      recommendations.push('Ensure exposure to natural daylight');
    }

    if (stats.sunExposureMinutes < 30) {
      recommendations.push('Need more sunlight (30+ minutes daily)');
    }

    if (currentLux > 50000) {
      recommendations.push('Extremely bright - wear sunglasses');
    }

    const brightness = getBrightnessLevel(currentLux);
    if (brightness === 'moderate' && currentHour >= 8 && currentHour < 18) {
      recommendations.push('Optimal lighting for work and reading');
    }

    if (stats.optimalLightingHours < 6) {
      recommendations.push('Improve lighting quality in workspace');
    }
  }

  return recommendations.length > 0
    ? recommendations
    : language === 'ar'
    ? ['مستوى الإضاءة جيد']
    : ['Lighting levels are good'];
}

/**
 * Get comprehensive light status
 */
export function getLightStatus(readings: LightReading[], currentLux: number = 300): LightStatus {
  const now = new Date();
  const currentHour = now.getHours();

  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);

  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);
  const yesterdayEnd = new Date(todayStart);

  const weekStart = new Date(now);
  weekStart.setDate(weekStart.getDate() - 7);

  const monthStart = new Date(now);
  monthStart.setMonth(monthStart.getMonth() - 1);

  const currentBrightness = getBrightnessLevel(currentLux);
  const isDaylightNow = isDaylight(currentLux);
  const timeOfDay = getTimeOfDay(currentHour, currentLux);

  const todayStats = getLightStatistics(readings, todayStart, now);
  const yesterdayStats = getLightStatistics(readings, yesterdayStart, yesterdayEnd);
  const weekStats = getLightStatistics(readings, weekStart, now);
  const monthStats = getLightStatistics(readings, monthStart, now);

  // Sun exposure calculation
  const recommendedSunMinutes = 30; // 30 minutes daily recommended
  const sunExposure = {
    todayMinutes: todayStats.sunExposureMinutes,
    weeklyMinutes: weekStats.sunExposureMinutes,
    recommendedMinutes: recommendedSunMinutes,
    isAdequate: todayStats.sunExposureMinutes >= recommendedSunMinutes,
  };

  // Circadian alignment assessment
  let circadianAlignment: 'good' | 'fair' | 'poor' = 'good';
  if (
    (currentHour >= 6 && currentHour < 9 && currentLux < 1000) ||
    ((currentHour >= 21 || currentHour < 6) && currentLux > 500)
  ) {
    circadianAlignment = 'poor';
  } else if (
    (currentHour >= 12 && currentHour < 18 && currentLux < 500) ||
    (currentHour >= 18 && currentHour < 21 && currentLux > 1000)
  ) {
    circadianAlignment = 'fair';
  }

  const recommendations = generateLightingRecommendations(currentLux, currentHour, todayStats);

  // Determine trend
  let trend: 'brightening' | 'stable' | 'dimming' = 'stable';
  if (todayStats.averageLux > yesterdayStats.averageLux * 1.1) {
    trend = 'brightening';
  } else if (todayStats.averageLux < yesterdayStats.averageLux * 0.9) {
    trend = 'dimming';
  }

  return {
    currentLux: Math.round(currentLux),
    currentBrightness,
    isDaylight: isDaylightNow,
    timeOfDay,
    sunExposure,
    circadianAlignment,
    todayStats,
    yesterdayStats,
    weekStats,
    monthStats,
    recommendations,
    trend,
  };
}

// ==================== Formatting Functions ====================

/**
 * Format lux value with unit
 */
export function formatLux(lux: number, language: 'en' | 'ar' = 'en'): string {
  if (language === 'ar') {
    if (lux >= 1000) {
      return `${(lux / 1000).toLocaleString('ar-EG', { maximumFractionDigits: 1 })} ألف لوكس`;
    }
    return `${lux.toLocaleString('ar-EG')} لوكس`;
  }

  if (lux >= 1000) {
    return `${(lux / 1000).toFixed(1)}k lux`;
  }
  return `${lux} lux`;
}

/**
 * Format brightness level text
 */
export function formatBrightnessLevel(
  level: BrightnessLevel,
  language: 'en' | 'ar' = 'en',
): string {
  if (language === 'ar') {
    const levels = {
      dark: 'مظلم',
      dim: 'خافت',
      moderate: 'متوسط',
      bright: 'ساطع',
      very_bright: 'ساطع جداً',
      extremely_bright: 'ساطع للغاية',
    };
    return levels[level];
  }

  const levels = {
    dark: 'Dark',
    dim: 'Dim',
    moderate: 'Moderate',
    bright: 'Bright',
    very_bright: 'Very Bright',
    extremely_bright: 'Extremely Bright',
  };
  return levels[level];
}

/**
 * Format time of day text
 */
export function formatTimeOfDay(time: TimeOfDay, language: 'en' | 'ar' = 'en'): string {
  if (language === 'ar') {
    const times = {
      night: 'ليل',
      dawn: 'فجر',
      morning: 'صباح',
      noon: 'ظهر',
      afternoon: 'بعد الظهر',
      dusk: 'غروب',
      evening: 'مساء',
    };
    return times[time];
  }

  const times = {
    night: 'Night',
    dawn: 'Dawn',
    morning: 'Morning',
    noon: 'Noon',
    afternoon: 'Afternoon',
    dusk: 'Dusk',
    evening: 'Evening',
  };
  return times[time];
}

/**
 * Format exposure time
 */
export function formatExposureTime(minutes: number, language: 'en' | 'ar' = 'en'): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (language === 'ar') {
    if (hours > 0) {
      return `${hours.toLocaleString('ar-EG')} س ${mins.toLocaleString('ar-EG')} د`;
    }
    return `${mins.toLocaleString('ar-EG')} دقيقة`;
  }

  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}min`;
}

/**
 * Format circadian alignment text
 */
export function formatCircadianAlignment(
  alignment: 'good' | 'fair' | 'poor',
  language: 'en' | 'ar' = 'en',
): string {
  if (language === 'ar') {
    const alignments = {
      good: 'جيد',
      fair: 'مقبول',
      poor: 'ضعيف',
    };
    return alignments[alignment];
  }

  const alignments = {
    good: 'Good',
    fair: 'Fair',
    poor: 'Poor',
  };
  return alignments[alignment];
}

/**
 * Format trend text
 */
export function formatTrend(
  trend: 'brightening' | 'stable' | 'dimming',
  language: 'en' | 'ar' = 'en',
): string {
  if (language === 'ar') {
    const trends = {
      brightening: 'يزداد سطوعاً',
      stable: 'مستقر',
      dimming: 'يخفت',
    };
    return trends[trend];
  }

  const trends = {
    brightening: 'Brightening',
    stable: 'Stable',
    dimming: 'Dimming',
  };
  return trends[trend];
}

/**
 * Get brightness level color
 */
export function getBrightnessColor(level: BrightnessLevel): string {
  const colors = {
    dark: '#1f2937', // Very dark gray
    dim: '#6b7280', // Gray
    moderate: '#f59e0b', // Orange
    bright: '#fbbf24', // Yellow
    very_bright: '#fde047', // Light yellow
    extremely_bright: '#fef08a', // Very light yellow
  };
  return colors[level];
}

/**
 * Get lux color based on value
 */
export function getLuxColor(lux: number): string {
  if (lux < 10) return '#1f2937'; // Dark
  if (lux < 50) return '#6b7280'; // Dim
  if (lux < 500) return '#f59e0b'; // Moderate
  if (lux < 10000) return '#fbbf24'; // Bright
  if (lux < 50000) return '#fde047'; // Very bright
  return '#fef08a'; // Extremely bright
}

/**
 * Get circadian alignment color
 */
export function getCircadianColor(alignment: 'good' | 'fair' | 'poor'): string {
  const colors = {
    good: '#10b981', // Green
    fair: '#f59e0b', // Orange
    poor: '#ef4444', // Red
  };
  return colors[alignment];
}
